import React from 'react'
import { motion } from 'framer-motion'
import { 
  Flame, 
  Beef, 
  Wheat, 
  Droplet,
  Info,
  CheckCircle,
  AlertTriangle
} from 'lucide-react'

interface NutritionInfoProps {
  nutrition: {
    calories: number
    protein: number
    carbs: number
    fat: number
    fiber: number
    sodium: number
  }
  dailyValues?: {
    calories: number
    protein: number
    carbs: number
    fat: number
    fiber: number
    sodium: number
  }
  showPercentages?: boolean
}

const NutritionInfo: React.FC<NutritionInfoProps> = ({ 
  nutrition, 
  dailyValues = {
    calories: 2000,
    protein: 50,
    carbs: 275,
    fat: 78,
    fiber: 28,
    sodium: 2300
  },
  showPercentages = true 
}) => {
  const calculatePercentage = (value: number, dailyValue: number) => {
    return Math.round((value / dailyValue) * 100)
  }

  const getNutrientColor = (_nutrient: string, percentage: number) => {
    if (percentage >= 20) return 'text-red-600 bg-red-50 border-red-200'
    if (percentage >= 10) return 'text-yellow-600 bg-yellow-50 border-yellow-200'
    return 'text-green-600 bg-green-50 border-green-200'
  }

  const getNutritionQuality = () => {
    const proteinPercentage = calculatePercentage(nutrition.protein, dailyValues.protein)
    const fiberPercentage = calculatePercentage(nutrition.fiber, dailyValues.fiber)
    const sodiumPercentage = calculatePercentage(nutrition.sodium, dailyValues.sodium)

    let score = 0
    const factors = []

    if (proteinPercentage >= 15) {
      score += 2
      factors.push('High protein content')
    }
    if (fiberPercentage >= 10) {
      score += 2
      factors.push('Good fiber content')
    }
    if (sodiumPercentage <= 15) {
      score += 1
      factors.push('Low sodium')
    }
    if (nutrition.calories <= 500) {
      score += 1
      factors.push('Reasonable calorie count')
    }

    return { score, factors }
  }

  const nutritionQuality = getNutritionQuality()

  const nutrients = [
    {
      name: 'Calories',
      value: nutrition.calories,
      unit: 'kcal',
      icon: Flame,
      dailyValue: dailyValues.calories,
      description: 'Energy content'
    },
    {
      name: 'Protein',
      value: nutrition.protein,
      unit: 'g',
      icon: Beef,
      dailyValue: dailyValues.protein,
      description: 'Muscle building and repair'
    },
    {
      name: 'Carbs',
      value: nutrition.carbs,
      unit: 'g',
      icon: Wheat,
      dailyValue: dailyValues.carbs,
      description: 'Primary energy source'
    },
    {
      name: 'Fat',
      value: nutrition.fat,
      unit: 'g',
      icon: Droplet,
      dailyValue: dailyValues.fat,
      description: 'Essential fatty acids'
    },
    {
      name: 'Fiber',
      value: nutrition.fiber,
      unit: 'g',
      icon: Wheat,
      dailyValue: dailyValues.fiber,
      description: 'Digestive health'
    },
    {
      name: 'Sodium',
      value: nutrition.sodium,
      unit: 'mg',
      icon: Info,
      dailyValue: dailyValues.sodium,
      description: 'Electrolyte balance'
    }
  ]

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3 }}
      className="border border-theme-border rounded-lg p-4 bg-theme-surface"
    >
      {/* Nutrition Quality Score */}
      <div className="mb-4 p-3 bg-theme-surface-light rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-theme-text-primary">Nutrition Quality Score</h4>
          <div className="flex items-center space-x-2">
            <div className={`px-2 py-1 rounded text-sm font-medium ${
              nutritionQuality.score >= 4 ? 'bg-green-100 text-green-800' :
              nutritionQuality.score >= 2 ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {nutritionQuality.score}/6
            </div>
            {nutritionQuality.score >= 4 ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
            )}
          </div>
        </div>
        {nutritionQuality.factors.length > 0 && (
          <div className="text-sm text-theme-text-secondary">
            <strong>Positive factors:</strong> {nutritionQuality.factors.join(', ')}
          </div>
        )}
      </div>

      {/* Nutrient Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {nutrients.map((nutrient) => {
          const percentage = calculatePercentage(nutrient.value, nutrient.dailyValue)
          const Icon = nutrient.icon
          
          return (
            <motion.div
              key={nutrient.name}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2, delay: nutrients.indexOf(nutrient) * 0.05 }}
              className={`p-3 rounded-lg border ${getNutrientColor(nutrient.name, percentage)}`}
            >
              <div className="flex items-center space-x-2 mb-2">
                <Icon className="w-4 h-4" />
                <span className="font-medium text-sm">{nutrient.name}</span>
              </div>
              
              <div className="space-y-1">
                <div className="text-lg font-bold">
                  {nutrient.value}{nutrient.unit}
                </div>
                
                {showPercentages && (
                  <div className="text-xs opacity-75">
                    {percentage}% daily value
                  </div>
                )}
                
                <div className="text-xs opacity-60">
                  {nutrient.description}
                </div>
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Daily Value Reference */}
      <div className="mt-4 pt-4 border-t border-theme-border">
        <p className="text-xs text-theme-text-muted">
          * Percent Daily Values are based on a {dailyValues.calories}-calorie diet. 
          Your daily values may be higher or lower depending on your calorie needs.
        </p>
      </div>

      {/* Health Recommendations */}
      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-center space-x-2 mb-2">
          <Info className="w-4 h-4 text-blue-600" />
          <h5 className="font-medium text-blue-900">Health Tips</h5>
        </div>
        <ul className="text-sm text-blue-800 space-y-1">
          {nutrition.protein < 10 && (
            <li>• Consider adding a protein source to make this a more complete meal</li>
          )}
          {nutrition.fiber < 3 && (
            <li>• Adding vegetables or whole grains can increase fiber content</li>
          )}
          {nutrition.sodium > 400 && (
            <li>• This meal is high in sodium - balance with lower-sodium choices throughout the day</li>
          )}
          {nutrition.calories > 600 && (
            <li>• This is a high-calorie meal - consider sharing or saving half for later</li>
          )}
          {nutritionQuality.score >= 4 && (
            <li>• Excellent nutritional balance! This meal provides good variety of nutrients</li>
          )}
        </ul>
      </div>
    </motion.div>
  )
}

export default NutritionInfo